import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChefHat, Printer, Loader2, ChevronLeft, ChevronRight, AlertCircle, MessageSquare, Sun, Moon, AlertTriangle, CalendarDays, Coffee, Check, Building2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { getPrintToolbarCSS, getPrintToolbarHTML } from '../utils/printHelper';

const MEALS = [
  { code: 'colazione', name: 'Colazione', icon: Coffee, color: 'amber' },
  { code: 'pranzo', name: 'Pranzo', icon: Sun, color: 'orange' },
  { code: 'cena', name: 'Cena', icon: Moon, color: 'indigo' }
];

export const KitchenPage = () => {
  const { api, activeStructure } = useAuth();
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealType, setMealType] = useState('pranzo');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const printRef = useRef(null);
  
  // Print options state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printMeals, setPrintMeals] = useState(['pranzo']); // Array of meals to print
  const [printGroupByHouse, setPrintGroupByHouse] = useState(true); // Raggruppa per casa
  const [houseFilter, setHouseFilter] = useState('all'); // Filtro per casa

  const structureName = activeStructure?.name || '';
  const isMultiHouse = activeStructure?.is_multi_house || false;
  const houses = activeStructure?.houses || [];

  const dateString = format(selectedDate, 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, categoriesRes, conditionsRes] = await Promise.all([
        api.get('/kitchen/summary', { params: { date: dateString } }),
        api.get('/categories'),
        api.get('/conditions')
      ]);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
      setConditions(conditionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [api, dateString]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigateDate = (direction) => {
    setSelectedDate(prev => addDays(prev, direction));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const handlePrint = () => {
    const summaryData = summary?.summary || { colazione: {}, pranzo: {}, cena: {} };
    const byHouseData = summary?.by_house || [];
    
    // Use selected meals for printing
    const mealsToPrint = printMeals.length > 0 ? printMeals : [mealType];
    const mealLabels = mealsToPrint.map(m => m === 'colazione' ? 'COLAZIONE' : m === 'pranzo' ? 'PRANZO' : 'CENA');
    const dateLabel = format(selectedDate, 'd MMMM yyyy', { locale: it });
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato - abilita i popup per stampare');
      return;
    }
    
    // Calculate total across all selected meals
    const grandTotal = mealsToPrint.reduce((acc, meal) => {
      const mealData = summaryData[meal] || {};
      return acc + Object.values(mealData).reduce((a, cat) => a + Object.values(cat.totals || {}).reduce((x, y) => x + y, 0), 0);
    }, 0);
    
    // Function to build meal sections HTML
    const buildMealSectionsHtml = (dataSource) => {
      let html = '';
      mealsToPrint.forEach(meal => {
        const currentMealData = dataSource[meal] || {};
        const mealLabel = meal === 'colazione' ? 'COLAZIONE' : meal === 'pranzo' ? 'PRANZO' : 'CENA';
        const mealTotal = Object.values(currentMealData).reduce((acc, cat) => acc + Object.values(cat.totals || {}).reduce((a, b) => a + b, 0), 0);
        
        if (mealTotal === 0) return;
        
        let categoriesHtml = '';
        categories.forEach(category => {
          const catData = currentMealData[category.code];
          if (!catData) return;
          const totals = catData.totals || {};
          const totalCount = Object.values(totals).reduce((a, b) => a + b, 0);
          if (totalCount === 0) return;
          
          let variantsHtml = '';
          Object.entries(totals).forEach(([variant, count]) => {
            variantsHtml += '<div class="variant-row variant-' + variant + '">' +
              '<span>' + variant.charAt(0).toUpperCase() + variant.slice(1) + '</span>' +
              '<span class="variant-count">' + count + '</span>' +
              '</div>';
          });
          
          categoriesHtml += '<div class="category-card">' +
            '<div class="category-header">' +
            '<span class="category-name">' + category.name + '</span>' +
            '<span class="category-total">' + totalCount + '</span>' +
            '</div>' + variantsHtml + '</div>';
        });
        
        let notesHtml = '';
        const hasNotes = Object.values(currentMealData).some(cat => cat.notes?.length > 0);
        if (hasNotes) {
          let notesItems = '';
          Object.entries(currentMealData).forEach(([catCode, catData]) => {
            (catData.notes || []).forEach(note => {
              const catName = categories.find(c => c.code === catCode)?.name || catCode;
              notesItems += '<div class="note-item"><strong>' + catName + '</strong> - Camera ' + 
                note.room + ' (' + note.patient + '): ' + note.note + '</div>';
            });
          });
          notesHtml = '<div class="notes-section"><div class="notes-title">⚠️ VARIAZIONI ' + mealLabel + '</div>' + notesItems + '</div>';
        }
        
        html += '<div class="meal-section">' +
          '<div class="meal-title meal-' + meal + '">' + mealLabel + ' - ' + mealTotal + ' piatti</div>' +
          '<div class="categories-grid">' + categoriesHtml + '</div>' + notesHtml + '</div>';
      });
      return html;
    };
    
    // Build content based on grouping
    let contentHtml = '';
    
    if (isMultiHouse && printGroupByHouse && byHouseData.length > 0) {
      // Group by house
      byHouseData.forEach(houseData => {
        const houseTotal = mealsToPrint.reduce((acc, meal) => {
          const mealData = houseData.summary[meal] || {};
          return acc + Object.values(mealData).reduce((a, cat) => a + Object.values(cat.totals || {}).reduce((x, y) => x + y, 0), 0);
        }, 0);
        
        if (houseTotal === 0) return;
        
        contentHtml += '<div class="house-section">' +
          '<div class="house-header">' +
          '<span class="house-prefix">' + houseData.house_prefix + '</span>' +
          '<span class="house-name">' + houseData.house_name + '</span>' +
          '<span class="house-total">' + houseTotal + ' piatti</span>' +
          '</div>' +
          buildMealSectionsHtml(houseData.summary) +
          '</div>';
      });
    } else {
      // Standard (no grouping by house)
      contentHtml = buildMealSectionsHtml(summaryData);
    }
    
    const html = '<!DOCTYPE html><html><head>' +
      '<title>Riepilogo Cucina - ' + mealLabels.join(' + ') + ' - ' + dateLabel + '</title>' +
      '<style>' +
      getPrintToolbarCSS() +
      '* { box-sizing: border-box; margin: 0; padding: 0; }' +
      'body { font-family: Arial, sans-serif; padding: 20px; }' +
      '.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }' +
      '.structure-name { font-size: 14px; font-weight: bold; color: #0284c7; margin-bottom: 5px; }' +
      '.header h1 { font-size: 24px; margin-bottom: 5px; }' +
      '.header p { font-size: 16px; color: #666; }' +
      '.total-badge { display: inline-block; background: #333; color: white; padding: 5px 15px; border-radius: 5px; font-size: 16px; margin-top: 10px; }' +
      // House section styles
      '.house-section { margin-bottom: 30px; padding: 15px; border: 2px solid #a855f7; border-radius: 10px; background: #faf5ff; page-break-inside: avoid; }' +
      '.house-header { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #d8b4fe; }' +
      '.house-prefix { background: #a855f7; color: white; padding: 6px 12px; border-radius: 6px; font-weight: bold; font-size: 18px; }' +
      '.house-name { font-size: 18px; font-weight: bold; color: #6b21a8; }' +
      '.house-total { margin-left: auto; background: #f3e8ff; padding: 4px 10px; border-radius: 4px; color: #7e22ce; font-weight: bold; }' +
      // Meal section styles
      '.meal-section { margin-bottom: 30px; page-break-inside: avoid; }' +
      '.meal-title { font-size: 20px; font-weight: bold; padding: 10px; margin-bottom: 15px; border-radius: 6px; color: white; }' +
      '.meal-colazione { background: #f59e0b; }' +
      '.meal-pranzo { background: #f97316; }' +
      '.meal-cena { background: #6366f1; }' +
      '.categories-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }' +
      '.category-card { border: 2px solid #ddd; padding: 12px; border-radius: 8px; background: white; }' +
      '.category-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; padding-bottom: 8px; margin-bottom: 8px; }' +
      '.category-name { font-size: 16px; font-weight: bold; }' +
      '.category-total { font-size: 24px; font-weight: bold; color: #0284c7; }' +
      '.variant-row { display: flex; justify-content: space-between; padding: 4px 8px; margin: 2px 0; border-radius: 4px; font-size: 14px; }' +
      '.variant-normale { background: #f1f5f9; }' +
      '.variant-tritato { background: rgba(147,51,234,0.1); color: #9333ea; }' +
      '.variant-pureato { background: rgba(124,58,237,0.1); color: #7c3aed; }' +
      '.variant-diabetico { background: rgba(234,88,12,0.1); color: #ea580c; }' +
      '.variant-count { font-weight: bold; font-size: 16px; }' +
      '.notes-section { margin-top: 20px; padding: 12px; background: #fef3c7; border: 2px solid #fcd34d; border-radius: 8px; }' +
      '.notes-title { font-size: 14px; font-weight: bold; color: #92400e; margin-bottom: 8px; }' +
      '.note-item { background: white; padding: 6px 8px; margin: 4px 0; border-radius: 4px; border-left: 3px solid #f59e0b; font-size: 12px; }' +
      '@media print { body { padding: 10px; } .meal-section, .house-section { page-break-inside: avoid; } }' +
      '</style></head><body>' +
      getPrintToolbarHTML('Riepilogo Cucina') +
      '<div class="print-content">' +
      '<div class="header">' +
      '<div class="structure-name">' + structureName + '</div>' +
      '<h1>RIEPILOGO CUCINA</h1>' +
      '<p>' + dateLabel + ' - ' + mealLabels.join(' + ') + '</p>' +
      '<div class="total-badge">Totale Giornata: ' + grandTotal + ' piatti</div>' +
      '</div>' + contentHtml +
      '</div></body></html>';
    
    printWindow.document.write(html);
    printWindow.document.close();
    setPrintDialogOpen(false);
  };

  const getCategoryName = (code) => {
    const cat = categories.find(c => c.code === code);
    return cat?.name || code;
  };

  const getVariantColor = (variant) => {
    switch (variant) {
      case 'tritato': return '#9333ea';
      case 'pureato': return '#7c3aed';
      case 'diabetico': return '#ea580c';
      default: return '#64748b';
    }
  };

  const getVariantLabel = (variant) => {
    switch (variant) {
      case 'tritato': return 'Tritato';
      case 'pureato': return 'Pureato';
      case 'diabetico': return 'Diabetico';
      default: return 'Normale';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  const summaryData = summary?.summary || { pranzo: {}, cena: {} };
  const byHouseData = summary?.by_house || [];
  
  // Get data based on house filter
  const getFilteredMealData = () => {
    if (houseFilter === 'all' || !isMultiHouse) {
      return summaryData[mealType] || {};
    }
    // Find specific house data
    const houseData = byHouseData.find(h => h.house_id === houseFilter);
    return houseData?.summary?.[mealType] || {};
  };
  
  const currentMealData = getFilteredMealData();
  const totalOrders = summary?.total_orders || 0;

  // Count orders for current meal type (filtered)
  const mealOrders = Object.values(currentMealData).reduce((acc, cat) => {
    return acc + Object.values(cat.totals || {}).reduce((a, b) => a + b, 0);
  }, 0);
  
  // Get selected house name for display
  const selectedHouseName = houseFilter !== 'all' 
    ? houses.find(h => h.id === houseFilter)?.name 
    : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kitchen-page">
      {/* Header - no-print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-blue-700" />
            Riepilogo Cucina
          </h1>
          <p className="text-slate-500 mt-1">Totali per categoria e preparazione</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Date Navigator with Calendar */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDate(-1)}
              data-testid="prev-date-btn"
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="px-2 sm:px-3 py-2 h-auto min-w-[120px] sm:min-w-[160px] hover:bg-slate-50"
                  data-testid="date-picker-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-1 sm:mr-2 text-blue-700" />
                  <span className="font-semibold text-slate-900 text-sm sm:text-base">
                    {format(selectedDate, 'EEE d MMM', { locale: it })}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={it}
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDate(1)}
              data-testid="next-date-btn"
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          <Button 
            onClick={() => {
              setPrintMeals([mealType]); // Default to current meal
              setPrintDialogOpen(true);
            }}
            className="bg-blue-700 hover:bg-blue-800 text-sm"
            size="sm"
            data-testid="print-kitchen-btn"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Stampa</span>
          </Button>
        </div>
      </div>

      {/* Print Options Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Opzioni Stampa Riepilogo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Seleziona i pasti da stampare</Label>
              <div className="space-y-2">
                {MEALS.map(meal => {
                  const Icon = meal.icon;
                  const isSelected = printMeals.includes(meal.code);
                  return (
                    <div 
                      key={meal.code}
                      onClick={() => {
                        if (isSelected) {
                          setPrintMeals(printMeals.filter(m => m !== meal.code));
                        } else {
                          setPrintMeals([...printMeals, meal.code]);
                        }
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? meal.color === 'amber' ? 'border-amber-500 bg-amber-50' :
                            meal.color === 'orange' ? 'border-orange-500 bg-orange-50' :
                            'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`p-2 rounded-full ${
                        isSelected
                          ? meal.color === 'amber' ? 'bg-amber-500 text-white' :
                            meal.color === 'orange' ? 'bg-orange-500 text-white' :
                            'bg-indigo-500 text-white'
                          : 'bg-slate-100'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`font-medium ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                        {meal.name}
                      </span>
                      {isSelected && <Check className="w-5 h-5 ml-auto text-green-600" />}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Opzione raggruppamento per casa - solo per strutture multi-casa */}
            {isMultiHouse && houses.length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  Raggruppamento
                </Label>
                <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all hover:border-purple-300"
                  onClick={() => setPrintGroupByHouse(!printGroupByHouse)}
                >
                  <input 
                    type="checkbox" 
                    checked={printGroupByHouse}
                    onChange={(e) => setPrintGroupByHouse(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <div>
                    <div className="font-medium text-slate-800 flex items-center gap-2">
                      <Home className="w-4 h-4 text-purple-500" />
                      Raggruppa per Casa
                    </div>
                    <p className="text-xs text-slate-500">I piatti saranno divisi per edificio/casa</p>
                  </div>
                </label>
              </div>
            )}
            
            <div className="pt-2 border-t">
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>Annulla</Button>
                <Button 
                  onClick={handlePrint} 
                  disabled={printMeals.length === 0}
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Stampa {printMeals.length > 0 ? `(${printMeals.length} past${printMeals.length === 1 ? 'o' : 'i'})` : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meal Type Toggle + House Filter - no-print */}
      <div className="flex flex-wrap items-center gap-3 no-print">
        <div className="flex gap-2">
          {MEALS.map(meal => {
            const Icon = meal.icon;
            const isActive = mealType === meal.code;
            return (
              <Button
                key={meal.code}
                variant={isActive ? 'default' : 'outline'}
                onClick={() => setMealType(meal.code)}
                className={isActive ? 
                  meal.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600' :
                  meal.color === 'orange' ? 'bg-orange-500 hover:bg-orange-600' :
                  'bg-indigo-500 hover:bg-indigo-600'
                : ''}
                data-testid={`meal-${meal.code}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {meal.name}
              </Button>
            );
          })}
        </div>

        {/* House Filter - Only for Multi-Casa structures */}
        {isMultiHouse && houses.length > 0 && (
          <Select value={houseFilter} onValueChange={setHouseFilter}>
            <SelectTrigger className="w-44 gap-2" data-testid="house-filter">
              <Home className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <SelectValue placeholder="Casa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le Case</SelectItem>
              {houses.filter(h => h.requires_meal_service !== false).map(house => (
                <SelectItem key={house.id} value={house.id}>
                  {house.prefix} - {house.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Print Header */}
      <div className="print-only hidden">
        <h1 className="text-2xl font-bold text-center mb-2">
          Riepilogo Cucina - {MEALS.find(m => m.code === mealType)?.name.toUpperCase() || 'PRANZO'}
        </h1>
        <p className="text-center text-base sm:text-lg mb-4 sm:mb-6">
          {format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
        </p>
      </div>

      {/* Total Badge */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        {(() => {
          const mealConfig = MEALS.find(m => m.code === mealType);
          const Icon = mealConfig?.icon || Sun;
          return (
            <Badge className={`px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-lg ${
              mealType === 'colazione' ? 'bg-amber-600' :
              mealType === 'pranzo' ? 'bg-orange-600' : 'bg-indigo-600'
            }`}>
              <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              {mealConfig?.name || 'Pranzo'}: {mealOrders} piatti
              {selectedHouseName && ` (${selectedHouseName})`}
            </Badge>
          );
        })()}
        <Badge variant="outline" className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-lg">
          Totale giornata: {totalOrders} piatti
        </Badge>
      </div>

      {mealOrders === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ChefHat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-500">
              Nessun ordine per {MEALS.find(m => m.code === mealType)?.name?.toLowerCase() || 'il pasto'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div ref={printRef} className="space-y-6">
          {/* Summary by Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {categories.map(category => {
              const catData = currentMealData[category.code];
              if (!catData) return null;

              const totals = catData.totals || {};
              const totalCount = Object.values(totals).reduce((a, b) => a + b, 0);
              const hasNotes = catData.notes && catData.notes.length > 0;

              return (
                <Card 
                  key={category.code} 
                  className="kitchen-stat-card"
                  data-testid={`kitchen-card-${category.code}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center justify-between">
                      {category.name}
                      <span className={`text-3xl font-bold ${mealType === 'pranzo' ? 'text-amber-600' : 'text-indigo-600'}`}>
                        {totalCount}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(totals).map(([variant, count]) => (
                        <div 
                          key={variant}
                          className="flex items-center justify-between py-1.5 px-3 rounded-lg"
                          style={{ backgroundColor: `${getVariantColor(variant)}10` }}
                        >
                          <span 
                            className="font-medium"
                            style={{ color: getVariantColor(variant) }}
                          >
                            {getVariantLabel(variant)}
                          </span>
                          <span 
                            className="text-xl font-bold"
                            style={{ color: getVariantColor(variant) }}
                          >
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>

                    {hasNotes && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2 text-amber-600 mb-2">
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-sm font-medium">Variazioni</span>
                        </div>
                        <div className="space-y-1">
                          {catData.notes.map((note, idx) => (
                            <div 
                              key={idx}
                              className="text-sm bg-amber-50 p-2 rounded border border-amber-200"
                            >
                              <span className="font-mono text-amber-800">{note.room}</span>
                              <span className="text-slate-600 mx-1">-</span>
                              <span className="text-slate-700">{note.note}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detailed Dishes */}
          <Card className="border-slate-200">
            <CardHeader className={`border-b border-slate-200 ${mealType === 'pranzo' ? 'bg-amber-50' : 'bg-indigo-50'}`}>
              <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                {mealType === 'pranzo' ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-indigo-500" />}
                Dettaglio Piatti - {mealType === 'pranzo' ? 'Pranzo' : 'Cena'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {categories.map(category => {
                  const catData = currentMealData[category.code];
                  if (!catData || !catData.dishes) return null;

                  return (
                    <div key={category.code} className="p-4">
                      <h3 className="font-semibold text-slate-700 mb-3">{category.name}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {Object.values(catData.dishes).map((dish, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div>
                              <span className="font-medium text-slate-800">{dish.name}</span>
                              <Badge 
                                variant="outline" 
                                className="ml-2"
                                style={{
                                  borderColor: getVariantColor(dish.variant),
                                  color: getVariantColor(dish.variant)
                                }}
                              >
                                {getVariantLabel(dish.variant)}
                              </Badge>
                              {dish.allergens?.length > 0 && (
                                <div className="flex items-center gap-1 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                                  <span className="text-xs font-mono text-amber-600">
                                    {dish.allergens.join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                            <span className="text-2xl font-bold text-slate-700">{dish.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* All Notes Section */}
          {Object.values(currentMealData).some(cat => cat.notes?.length > 0) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="border-b border-amber-200">
                <CardTitle className="text-lg font-semibold text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Tutte le Variazioni - {mealType === 'pranzo' ? 'Pranzo' : 'Cena'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {Object.entries(currentMealData).map(([catCode, catData]) => (
                    catData.notes?.map((note, idx) => (
                      <div 
                        key={`${catCode}-${idx}`}
                        className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-200"
                      >
                        <Badge variant="outline" className="shrink-0">
                          {getCategoryName(catCode)}
                        </Badge>
                        <div className="flex-1">
                          <span className="font-mono text-sm text-slate-600">{note.room}</span>
                          <span className="mx-2 text-slate-400">|</span>
                          <span className="font-medium text-slate-800">{note.patient}</span>
                          <p className="text-slate-600 mt-1">{note.note}</p>
                        </div>
                      </div>
                    ))
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
